import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './BallAvoider.css';

const BallAvoider = () => {
  const [playerPos, setPlayerPos] = useState({ x: 250, y: 250 });
  const [obstacles, setObstacles] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [keys, setKeys] = useState({});
  const animationRef = useRef(null);

  const GAME_SIZE = 500;
  const PLAYER_SIZE = 20;
  const PLAYER_SPEED = 5;

  const createObstacle = () => {
    const side = Math.floor(Math.random() * 4);
    let x, y;
    
    switch(side) {
      case 0: x = Math.random() * GAME_SIZE; y = -20; break;
      case 1: x = GAME_SIZE + 20; y = Math.random() * GAME_SIZE; break;
      case 2: x = Math.random() * GAME_SIZE; y = GAME_SIZE + 20; break;
      default: x = -20; y = Math.random() * GAME_SIZE;
    }

    const angle = Math.atan2(250 - y, 250 - x);
    const speed = 2 + Math.random() * 2;
    
    return {
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      id: Date.now() + Math.random()
    };
  };

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setPlayerPos({ x: 250, y: 250 });
    setObstacles([]);
    setScore(0);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowleft', 'arrowdown', 'arrowright'].includes(key)) {
        e.preventDefault();
        setKeys(k => ({ ...k, [key]: true }));
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      setKeys(k => ({ ...k, [key]: false }));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const spawnInterval = setInterval(() => {
      setObstacles(o => [...o, createObstacle()]);
    }, 1000);

    return () => clearInterval(spawnInterval);
  }, [gameActive, gameOver]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const gameLoop = () => {
      setPlayerPos(pos => {
        let newX = pos.x;
        let newY = pos.y;

        if (keys['w'] || keys['arrowup']) newY = Math.max(0, newY - PLAYER_SPEED);
        if (keys['s'] || keys['arrowdown']) newY = Math.min(GAME_SIZE - PLAYER_SIZE, newY + PLAYER_SPEED);
        if (keys['a'] || keys['arrowleft']) newX = Math.max(0, newX - PLAYER_SPEED);
        if (keys['d'] || keys['arrowright']) newX = Math.min(GAME_SIZE - PLAYER_SIZE, newX + PLAYER_SPEED);

        return { x: newX, y: newY };
      });

      setObstacles(obs => {
        const newObstacles = obs
          .map(o => ({ ...o, x: o.x + o.vx, y: o.y + o.vy }))
          .filter(o => o.x > -50 && o.x < GAME_SIZE + 50 && o.y > -50 && o.y < GAME_SIZE + 50);

        newObstacles.forEach(o => {
          const dist = Math.hypot(o.x - playerPos.x, o.y - playerPos.y);
          if (dist < 25) {
            setGameActive(false);
            setGameOver(true);
          }
        });

        return newObstacles;
      });

      setScore(s => s + 1);
      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationRef.current);
  }, [gameActive, gameOver, keys, playerPos]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">⚽ Ball Avoider</h1>
        <div className="game-instructions">
          Use WASD or arrow keys to dodge obstacles!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {Math.floor(score / 10)}</div>
        
        <div className="game-play-area">
          <div className="avoider-game-area">
            <div 
              className="player-ball" 
              style={{ left: playerPos.x, top: playerPos.y }}
            >
              😊
            </div>
            
            {obstacles.map(obs => (
              <div 
                key={obs.id} 
                className="obstacle-ball" 
                style={{ left: obs.x, top: obs.y }}
              >
                ⚫
              </div>
            ))}

            {!gameActive && (
              <div className="game-status-overlay">
                {gameOver ? (
                  <div className="status-message lose">
                    Game Over! Score: {Math.floor(score / 10)}
                  </div>
                ) : (
                  <div className="status-message start">Press Start!</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default BallAvoider;
